import { CheckCircle2, ChevronRight, TrendingUp, XCircle } from "lucide-react";
import React, { useState } from "react";

import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button";
import { FitCheckAssessment } from "@/app/Views/FitCheck/config/api";
import FitCheck from "@/app/Views/FitCheck/FitCheck";

import { JobDetails } from "../utils/helpers";

type MatchLevel = "high" | "partial" | "low" | "none";
type FitCheckState = "idle" | "loading" | "active" | "results";

/**
 * Idle-state pitches. One is picked at random per mount, so the card can test which
 * angle moves readers while a single reader keeps one pitch for the whole page view.
 */
const idleVariants = [
	{
		header: "One check before you apply",
		body: "We compare your resume to this job description and show you what is missing.",
	},
	{
		header: "Most resumes are filtered out before a person reads them",
		body: "Make sure yours is not one of them. Let's check it against this posting first.",
	},
	{
		header: "Find the gaps before the employer does",
		body: "We check your resume against every requirement in this posting, then show you what to fix.",
	},
	{
		header: "Do not waste this application",
		body: "You get one chance with this employer. See how your resume matches the role, then fix the weak parts.",
	},
	{
		header: "The same resume will not win every job",
		body: "See how yours reads against this exact role, and what to change for it.",
	},
	{
		header: "Does your resume prove you can do this job?",
		body: "We check your experience against each requirement and show what is not clear yet.",
	},
	{
		header: "Will this resume get you a callback?",
		body: "Check it against this job description and fix what is weak before you apply.",
	},
	{
		header: "Many people will apply. Few will match.",
		body: "See how your resume lines up with this role, and what puts you on the shortlist.",
	},
];

/**
 * Micro-copy for FitCheck section
 */
const fitCheckCopy = {
	idle: {
		cta: "Check my fit",
	},
	loading: {
		header: "Checking your fit...",
		body: "Looking for your profile and any previous assessments.",
	},
	active: {
		header: "Check your fit",
		body: "Select your resume to analyze your fit for this role",
		cancel: "Cancel",
	},
	highMatch: {
		header: "Strong match",
		badge: "Ready to apply",
		body: "Your experience aligns well with this role. You're well-positioned to apply with confidence.",
		strengths: "Your key strengths",
		applyCta: "Apply for this job",
		viewCta: "View detailed results",
	},
	partialMatch: {
		header: "Potential match",
		body: "You meet several key requirements. A few gaps are worth reviewing before applying.",
		gaps: "Areas to consider",
		cta: "View my FitCheck results",
	},
	lowMatch: {
		header: "Not the best match — yet",
		body: "This role may be challenging based on your current profile. We've highlighted areas to improve or similar roles to explore.",
		focusAreas: "Areas to develop",
		cta: "View recommendations",
	},
};

interface FitCheckSectionProps {
	jobDetails: JobDetails;
	fitCheckResult?: FitCheckAssessment | null;
	hasFitCheckResult: boolean;
	onFitCheckClick: () => boolean | void;
	onApplyClick?: () => void;
	hideIdleCTA?: boolean;
	startSignal?: number;
	isRunning?: boolean;
}

/**
 * Determine match level based on FitCheck assessment
 */
function getMatchLevel(assessment: FitCheckAssessment): MatchLevel {
	const { baselineCheck } = assessment;
	const statuses = [
		baselineCheck.education.status,
		baselineCheck.experience.status,
		baselineCheck.skills.status,
		baselineCheck.certification.status,
	];

	const meetCount = statuses.filter((s) => s === "MEET").length;
	const missingCount = statuses.filter((s) => s === "MISSING").length;

	if (meetCount >= 3) return "high";
	if (missingCount >= 3) return "low";
	return "partial";
}

export function FitCheckSection({
	jobDetails,
	fitCheckResult,
	hasFitCheckResult,
	onFitCheckClick,
	onApplyClick,
	hideIdleCTA = false,
	startSignal = 0,
	isRunning = false,
}: FitCheckSectionProps) {
	const [fitCheckState, setFitCheckState] = useState<FitCheckState>("idle");
	// Chosen once per mount: a pitch that changed on every render would be unreadable.
	const [idleCopy] = useState(() => idleVariants[Math.floor(Math.random() * idleVariants.length)]);

	// Expose a method to trigger FitCheck from outside (via ref or global event)
	// Listen for custom event to start FitCheck
	React.useEffect(() => {
		const handleStartFitCheck = () => {
			setFitCheckState("loading");
			setTimeout(() => {
				setFitCheckState("active");
			}, 500);
		};

		window.addEventListener("startFitCheck", handleStartFitCheck);
		return () => window.removeEventListener("startFitCheck", handleStartFitCheck);
	}, []);

	// Handle FitCheck button click - show inline FitCheck.
	const handleStartFitCheck = () => {
		setFitCheckState("loading");
		// Short delay to show loading, then show FitCheck
		setTimeout(() => {
			setFitCheckState("active");
		}, 500);
	};

	const startedSignalRef = React.useRef(startSignal);
	React.useEffect(() => {
		if (startSignal === startedSignalRef.current) return;
		startedSignalRef.current = startSignal;
		setFitCheckState("active");
	}, [startSignal]);

	const handleIdleCTAClick = () => {
		if (onFitCheckClick() === false) return;
		handleStartFitCheck();
	};

	// Loading state - checking for existing FitCheck
	if (isRunning || fitCheckState === "loading") {
		return (
			<div className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 mb-6">
				<div className="flex flex-col items-center justify-center py-8 gap-4">
					<div className="w-8 h-8">
						<Spinner />
					</div>
					<div className="text-center">
						<h3 className="text-lg font-semibold text-gray-900 mb-1">{fitCheckCopy.loading.header}</h3>
						<p className="text-sm text-gray-600">{fitCheckCopy.loading.body}</p>
					</div>
				</div>
			</div>
		);
	}

	// Active FitCheck - show the FitCheck component inline
	if (fitCheckState === "active") {
		return (
			<div id="fitcheck-section" className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
				<div className="bg-linear-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-purple-100">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<img src="/images/filling-survey.png" className="w-10 h-10 object-contain" alt="" />
							<div>
								<h3 className="text-lg font-semibold text-gray-900">{fitCheckCopy.active.header}</h3>
								<p className="text-xs text-gray-500">{fitCheckCopy.active.body}</p>
							</div>
						</div>
						<Button variant="ghost" size="sm" onClick={() => setFitCheckState("idle")}>
							{fitCheckCopy.active.cancel}
						</Button>
					</div>
				</div>
				<div className="p-4 min-h-[200px]">
					<FitCheck jobDetails={jobDetails as unknown as Parameters<typeof FitCheck>[0]["jobDetails"]} />
				</div>
			</div>
		);
	}

	// Has FitCheck result - show based on match level
	if (hasFitCheckResult && fitCheckResult) {
		const matchLevel = getMatchLevel(fitCheckResult);

		if (matchLevel === "high") {
			return <HighMatchState fitCheck={fitCheckResult} onApplyClick={onApplyClick} onViewAgain={handleStartFitCheck} />;
		}

		if (matchLevel === "partial") {
			return <PartialMatchState fitCheck={fitCheckResult} onViewAgain={handleStartFitCheck} />;
		}

		return <LowMatchState fitCheck={fitCheckResult} onViewAgain={handleStartFitCheck} />;
	}

	// Idle state - No FitCheck result yet
	// If hideIdleCTA is true, don't render the idle CTA (it's shown elsewhere)
	if (hideIdleCTA) {
		return null;
	}

	// Show CTA with image
	return (
		<div
			id="fitcheck-section"
			className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 mb-6"
		>
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
				<img src="/images/filling-survey.png" className="w-24 sm:w-32" alt="Check your fit" />
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-gray-900 mb-1">{idleCopy.header}</h3>
					<p className="text-sm text-gray-600 mb-2">{idleCopy.body}</p>
				</div>
				<Button onClick={handleIdleCTAClick} size="sm" variant="outlineSecondary">
					{fitCheckCopy.idle.cta}
				</Button>
			</div>
		</div>
	);
}

// High Match State Component
interface MatchStateProps {
	fitCheck: FitCheckAssessment;
	onApplyClick?: () => void;
	onViewAgain?: () => void;
}

function HighMatchState({ fitCheck, onApplyClick, onViewAgain }: MatchStateProps) {
	return (
		<div className="bg-linear-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-green-100 rounded-lg">
					<CheckCircle2 className="h-6 w-6 text-green-600" />
				</div>
				<div className="flex-1">
					<div className="flex items-center gap-2 mb-1">
						<h3 className="text-lg font-semibold text-green-800">{fitCheckCopy.highMatch.header}</h3>
						<span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
							{fitCheckCopy.highMatch.badge}
						</span>
					</div>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.highMatch.body}</p>

					{/* Key strengths */}
					{fitCheck.strengths.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">{fitCheckCopy.highMatch.strengths}</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.strengths.slice(0, 3).map((strength, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-green-200">
										{strength}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onApplyClick && (
							<Button onClick={onApplyClick} className="bg-green-600 hover:bg-green-700 text-white">
								{fitCheckCopy.highMatch.applyCta}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
						{onViewAgain && (
							<Button onClick={onViewAgain} variant="outline" size="sm">
								{fitCheckCopy.highMatch.viewCta}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// Partial Match State Component
function PartialMatchState({ fitCheck, onViewAgain }: Omit<MatchStateProps, "onApplyClick">) {
	return (
		<div className="bg-linear-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-amber-100 rounded-lg">
					<TrendingUp className="h-6 w-6 text-amber-600" />
				</div>
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-amber-800 mb-1">{fitCheckCopy.partialMatch.header}</h3>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.partialMatch.body}</p>

					{/* Show gaps if any */}
					{fitCheck.gaps.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">{fitCheckCopy.partialMatch.gaps}</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.gaps.slice(0, 2).map((gap, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-amber-200">
										{gap}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onViewAgain && (
							<Button onClick={onViewAgain} className="bg-amber-600 hover:bg-amber-700 text-white">
								{fitCheckCopy.partialMatch.cta}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// Low Match State Component
function LowMatchState({ fitCheck, onViewAgain }: Omit<MatchStateProps, "onApplyClick">) {
	return (
		<div className="bg-linear-to-r from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-gray-100 rounded-lg">
					<XCircle className="h-6 w-6 text-gray-500" />
				</div>
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-gray-800 mb-1">{fitCheckCopy.lowMatch.header}</h3>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.lowMatch.body}</p>

					{/* Focus areas */}
					{fitCheck.focusAreas && fitCheck.focusAreas.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">{fitCheckCopy.lowMatch.focusAreas}</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.focusAreas.slice(0, 3).map((area, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-gray-200">
										{area}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onViewAgain && (
							<Button onClick={onViewAgain} variant="outline">
								{fitCheckCopy.lowMatch.cta}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
					</div>

					{/* Encouragement note */}
					{fitCheck.encouragementNote && (
						<p className="mt-4 text-xs text-gray-500 italic border-t border-gray-200 pt-3">
							{fitCheck.encouragementNote}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
